import React, { useContext } from "react";
import { motion } from "framer-motion";
import { transition1 } from "../transitions";
import { CursorContext } from "../context/CursorContext";
import { ScrollLink } from "../components/ScrollLink";
import { BsArrowRight } from "react-icons/bs";
import { about, experiences, tags } from "../Data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.65,
      ease: "easeOut",
    },
  }),
};

const About = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  return (
    <motion.section
      id="about"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 pb-20 overflow-y-auto"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-5xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            About me
          </motion.h1>

          {/* ── Bio ── */}
          <motion.p
            className="mt-3 mb-2 text-sm leading-relaxed text-justify max-w-4xl"
            variants={fadeUp}
            custom={1}
          >
            {/* Hey, I'm{" "}
            <span className="bg-gradient-to-r from-purple-400 to-purple-300 bg-clip-text text-transparent">
              Guru Leo
            </span>
            🍃 */}
            <span className="lg-text">{about.text}</span>
            <span className="mob-text">{about.sortText}</span>
          </motion.p>
        </motion.div>

        {/* ── AI Focus Tags ── */}
        <motion.div
          className="mb-6 flex flex-wrap gap-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={fadeUp}
          custom={2}
        >
          {tags.map((tag, i) => (
            <motion.span
              key={i}
              className="flex items-center gap-1.5 rounded-full bg-purple-400/10 px-3 py-1 text-xs font-medium text-purple-700 dark:text-purple-200 border border-purple-400/20"
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + i * 0.06 }}
              viewport={{ once: false }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block" />
              {tag}
            </motion.span>
          ))}
        </motion.div>

        {/* ── Experience label ── */}
        <motion.div
          className="mb-3"
          variants={fadeUp}
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <span className="relative inline-block text-[0.95rem] font-semibold tracking-wide after:content-[''] after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-1/2 after:bg-purple-400 after:rounded">
            Experience
          </span>
        </motion.div>

        {/* ── Timeline ── */}
        <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent">
          <div className="relative pl-5">
            {/* vertical line */}
            <div className="absolute left-5 top-0 bottom-0 w-[2px] bg-purple-200 dark:bg-purple-700" />

            {experiences.map((exp, i) => (
              <motion.div
                key={i}
                className="relative pl-7 mb-7"
                variants={fadeUp}
                custom={i + 4}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.15 }}
              >
                {/* dot */}
                <div
                  className={`absolute left-[-4px] top-1.5 h-3 w-3 rounded-full z-10 ${
                    exp.isNew
                      ? "bg-purple-400 ring-2 ring-purple-400/30"
                      : "bg-purple-400"
                  }`}
                />

                {/* date + badge */}
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                    {exp.date}
                  </span>
                  {exp.isNew && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-400/15 text-purple-500 font-semibold uppercase tracking-wider">
                      Current
                    </span>
                  )}
                </div>

                <div className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  {exp.title}
                </div>
                <p className="text-xs leading-relaxed text-justify text-slate-500 dark:text-slate-400 pr-2">
                  {exp.desc}
                </p>

                {/* tags */}
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {exp.tags.map((tag, j) => (
                    <li key={j}>
                      <div className="rounded-full bg-purple-400/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 dark:text-purple-200">
                        {tag}
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <motion.div
          className="shrink-0 mt-4 mb-1"
          variants={fadeUp}
          custom={8}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <ScrollLink
            section="portfolio"
            className="btn max-w-fit hover:scale-105 transition-transform duration-300"
          >
            View My Work
            <span className="ml-1">
              <BsArrowRight />
            </span>
          </ScrollLink>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default About;
